import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { usePdfHighlighterContext } from '~/components/Shared/EcPdfViewerHighlighter/components/PdfHighlighterContext';
import { Box } from '~/eds/src/components/Box/Box';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { KeyboardEventCodeType } from '~/enums/KeyboardEventCodeType';
import { Nullable } from '~/types/Nullable';
import theme from '~/ui/theme';

interface Props {
  isVisible: boolean;
  listenOn: React.MutableRefObject<HTMLElement | null>;
  toggleVisiblity: () => void;
}

type MatchesCount = {
  current: number;
  total: number;
};

export enum SearchTypeEnum {
  Find = 'find',
  FindAgain = 'again',
}

export enum SearchResultEnum {
  Found = 0,
  NotFound = 1,
  Wrapped = 2,
  Pending = 3,
}

type ControlStateArgs = {
  state: SearchResultEnum;
  matchesCount: MatchesCount;
};

const SearchBar = ({ isVisible, listenOn, toggleVisiblity }: Props) => {
  const intl = useIntl();
  const [searchText, setSearchText] = useState<Nullable<string>>('');
  const [matchesCount, setMatchesCount] = useState<MatchesCount>({
    current: 0,
    total: 0,
  });
  const { eventBus, viewer } = usePdfHighlighterContext();

  const shouldShowEndOfSearchMessage =
    matchesCount.total > 0 &&
    matchesCount.current === matchesCount.total &&
    searchText;

  // eventListeners doesn't access the mutated state, so we use a ref to get the updated value
  const searchTextRef = useRef(searchText);
  const showRef = useRef(isVisible);

  const nextSearch = (previousEnabled = false) => {
    eventBus?.dispatch(SearchTypeEnum.Find, {
      type: SearchTypeEnum.FindAgain,
      query: searchTextRef.current,
      phraseSearch: true,
      findPrevious: previousEnabled,
      highlightAll: true,
    });
  };

  const triggerSearch = (searchValue: Nullable<string>) => {
    eventBus?.dispatch(SearchTypeEnum.Find, {
      query: searchValue,
      phraseSearch: true,
      highlightAll: true,
    });
  };

  const triggerNextOnEnter = (event: KeyboardEvent) => {
    if (event.code === KeyboardEventCodeType.Enter) {
      nextSearch(event.shiftKey);
    }
  };

  const closeSearchBar = (event: KeyboardEvent) => {
    if (event.code === KeyboardEventCodeType.Escape) {
      if (showRef.current) {
        showRef.current = false;
        toggleSearchBar();
        clearSearch();
      }
    }
  };

  const interceptDefaultSearch = (e: KeyboardEvent) => {
    if (
      e.code === KeyboardEventCodeType.F3 ||
      ((e.ctrlKey || e.metaKey) && e.code === KeyboardEventCodeType.F)
    ) {
      e.preventDefault();
      showRef.current = !showRef.current;
      toggleSearchBar();
    }
  };

  const toggleSearchBar = () => {
    if (!showRef.current) {
      setTimeout(() => listenOn.current?.focus(), 100);
    }
    toggleVisiblity();
  };

  const clearSearch = () => {
    triggerSearch('');
  };

  useEffect(() => {
    showRef.current = isVisible;
    let timeoutId: ReturnType<typeof setTimeout>;
    if (!isVisible) {
      searchText && clearSearch();
    } else {
      // popover calls for focus so both calls will be triggering in random order, we wait a couple ms to get priority.
      timeoutId = setTimeout(
        () => document.getElementById('search-field-input')?.focus(),
        100,
      );
    }

    return () => clearTimeout(timeoutId);
  }, [isVisible]);

  const setZIndexToHighlightedWords = () => {
    const searchHighlights = document.querySelectorAll<HTMLElement>(
      '.PdfHighlighter span',
    );
    searchHighlights.forEach((spans) => {
      // Sets the search highlight to have zIndex higher than all highlights
      spans.style.zIndex = `${theme.zIndices.highlight['high:active'] + 1}`;
    });
  };

  useEffect(() => {
    listenOn.current?.setAttribute?.('tabIndex', '0');
    listenOn.current?.addEventListener('keydown', interceptDefaultSearch);
    listenOn.current?.addEventListener('keydown', triggerNextOnEnter);
    listenOn.current?.addEventListener('keydown', closeSearchBar);

    return () => {
      listenOn.current?.removeEventListener('keydown', interceptDefaultSearch);
      listenOn.current?.removeEventListener('keydown', triggerNextOnEnter);
      listenOn.current?.removeEventListener('keydown', closeSearchBar);
    };
  }, []);

  useEffect(() => {
    const updateFindControlStateListener = (arg: ControlStateArgs) => {
      if (arg.state !== SearchResultEnum.Pending) {
        setMatchesCount(arg.matchesCount);
        setZIndexToHighlightedWords();
      }
    };

    const updateFindMatchesCountListener = (
      arg: Omit<ControlStateArgs, 'state'>,
    ) => {
      setMatchesCount(arg.matchesCount);
    };

    if (eventBus) {
      eventBus.on('updatefindcontrolstate', updateFindControlStateListener);
      eventBus.on('updatefindmatchescount', updateFindMatchesCountListener);
    }

    return () => {
      if (eventBus) {
        eventBus.off('updatefindcontrolstate', updateFindControlStateListener);
        eventBus.off('updatefindmatchescount', updateFindMatchesCountListener);
      }
    };
  }, [eventBus]);

  useEffect(() => {
    if (viewer?.findController && isVisible) {
      searchTextRef.current = searchText;
      triggerSearch(searchText);
    }
  }, [searchText, isVisible]);

  return (
    <Box
      styles={{
        width: 0,
        opacity: 0,
        border: 'none',
        overflow: 'hidden',
        transition: 'width 0.5s ease-out, opacity 0.4s ease-out',
        display: 'inline-block',
        ...(isVisible
          ? {
              width: searchText ? 550 : 400,
              opacity: 1,
            }
          : undefined),
      }}
    >
      <Layout border="border" borderRadius="m" p={1} px={4}>
        <Layout direction="column" spacing={1}>
          <Layout spacing={3} align="center">
            <TextInput
              bg="inherit"
              id="search-field-input"
              name="pdfSearchField"
              placeholder={intl.formatMessage({
                id: 'PdfSearchBar.typeToFind',
                defaultMessage: 'Type to find',
                description: 'Placeholder for PDF search input',
              })}
              value={searchText}
              onChange={setSearchText}
            />
            <IconButton
              icon="chevron-up"
              tooltip={intl.formatMessage({
                id: 'PdfSearchBar.previous',
                defaultMessage: 'Previous',
                description: 'Tooltip for previous search result button',
              })}
              onClick={() => nextSearch(true)}
            />
            <IconButton
              icon="chevron-down"
              tooltip={intl.formatMessage({
                id: 'PdfSearchBar.next',
                defaultMessage: 'Next',
                description: 'Tooltip for next search result button',
              })}
              onClick={() => nextSearch()}
            />
            {searchText && (
              <Chip
                text={intl.formatMessage(
                  {
                    id: 'PdfSearchBar.matchCount',
                    defaultMessage:
                      '{current} of {total} {total, plural, one {match} other {matches}}',
                    description:
                      'Chip showing current and total matches in PDF search. Example: current = 2, total = 5',
                  },
                  {
                    current: matchesCount.current,
                    total: matchesCount.total,
                  },
                )}
              />
            )}
          </Layout>
          {shouldShowEndOfSearchMessage && (
            <Text preset="error" flex="flex" variant="tiny">
              {intl.formatMessage({
                id: 'PdfSearchBar.reachedLastResult',
                defaultMessage: 'Reached last result, continue from top',
                description:
                  'Message shown when the last search result is reached in PDF search',
              })}
            </Text>
          )}
        </Layout>
      </Layout>
    </Box>
  );
};

export default SearchBar;
